import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { createApiContext } from "../../../shared/api/create-api-context";
import type {
  Assessment,
  AssessmentInput,
  BodyApi,
  PhotoRow,
  PhotoUploadInput,
} from "./body-api";

const context = createApiContext<BodyApi>("Body");
export const BodyApiProvider = context.Provider;
export const useBodyApi = context.useApi;

export const bodyKeys = {
  all: (profileId: string) => ["body", profileId] as const,
  assessments: (profileId: string, page: number) =>
    [...bodyKeys.all(profileId), "assessments", page] as const,
  photos: (profileId: string, paths: string[]) =>
    [...bodyKeys.all(profileId), "photo-urls", [...paths].sort()] as const,
};

export function useAssessments(profileId: string, page = 0): UseQueryResult<Assessment[]> {
  const api = useBodyApi();
  return useQuery({
    queryKey: bodyKeys.assessments(profileId, page),
    queryFn: ({ signal }) => api.assessments(profileId, page, signal),
    enabled: profileId !== "",
  });
}

/**
 * URLs assinadas expiram; a query as renova antes disso e nunca as persiste
 * fora da memória do processo.
 */
export function usePhotoUrls(
  profileId: string,
  paths: string[],
  seconds: number | null,
): UseQueryResult<Map<string, string>> {
  const api = useBodyApi();
  return useQuery({
    queryKey: bodyKeys.photos(profileId, paths),
    queryFn: ({ signal }) => api.photoUrls(paths, seconds, signal),
    enabled: paths.length > 0,
    gcTime: 0,
    staleTime: Math.max(0, (seconds ?? 300) - 30) * 1000,
  });
}

export function useSaveAssessment(userId: string, profileId: string) {
  const api = useBodyApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AssessmentInput) => api.saveAssessment(userId, input),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: bodyKeys.all(profileId) });
    },
  });
}

export function useUploadPhoto(userId: string, profileId: string) {
  const api = useBodyApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PhotoUploadInput) => api.uploadPhoto(userId, input),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: bodyKeys.all(profileId) });
    },
  });
}

export function useDeletePhoto(profileId: string) {
  const api = useBodyApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (photo: Pick<PhotoRow, "id" | "storage_path">) => api.deletePhoto(photo),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: bodyKeys.all(profileId) });
    },
  });
}

export function useSetPhotoAuthorization(profileId: string) {
  const api = useBodyApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { photoId: string; authorized: boolean }) =>
      api.setPhotoAuthorization(input.photoId, input.authorized),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: bodyKeys.all(profileId) });
    },
  });
}

export function useDeleteAssessment(profileId: string) {
  const api = useBodyApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assessment: Assessment) => api.deleteAssessment(assessment),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: bodyKeys.all(profileId) });
    },
  });
}
